import * as Yup from 'yup';
import { LoanStatus } from '../../../types/loan/LoanStatus';
import { numericUtils } from '../../../utils/numeric.utils';

// number().nullable() treats '' as NaN, so map empty strings to null.
// Strip thousands separators so values like '50,345' parse as numbers.
const nullableNumber = () =>
    Yup.number()
        .nullable()
        .transform((value, originalValue) => {
            if (typeof originalValue !== 'string') {
                return value;
            }
            const cleaned = originalValue.replace(/,/g, '').trim();
            return cleaned === '' ? null : Number(cleaned);
        });

const validRange = (minField: string, maxField: string) => ({
    name: 'valid-range',
    message: 'Invalid range',
    test(_value: number | null | undefined, context: Yup.TestContext) {
        const min = numericUtils.numberOrDefault(context.parent[minField], null);
        const max = numericUtils.numberOrDefault(context.parent[maxField], null);
        return min == null || max == null || max >= min;
    },
});

export const loanValidationSchema = Yup.object().shape({
    issuerName: Yup.string().trim().required('Issuer is required').max(256, 'Max 256 characters'),
    status: Yup.mixed<LoanStatus>().oneOf(Object.values(LoanStatus)).required('Status is required'),
    dueDate: Yup.string()
        .nullable()
        .when('dueTime', {
            is: (dueTime?: string | null) => !!dueTime,
            then: schema => schema.required('Due Date is required'),
        }),
    size: nullableNumber().moreThan(0, 'Size must be greater than 0'),
    addOn: nullableNumber().moreThan(0, 'Add-on must be greater than 0'),
    guidanceMin: nullableNumber().test(validRange('guidanceMin', 'guidanceMax')),
    guidanceMax: nullableNumber().test(validRange('guidanceMin', 'guidanceMax')),
    priceMin: nullableNumber().max(150, 'Max 150.000').test(validRange('priceMin', 'priceMax')),
    priceMax: nullableNumber().max(150, 'Max 150.000').test(validRange('priceMin', 'priceMax')),
    floor: Yup.number().nullable().max(100, 'Max 100.00'),
    margin: Yup.number().nullable().max(100, 'Max 100.000'),
    callProtection: Yup.string().nullable().max(256, 'Max 256 characters'),
    facility: Yup.string().nullable().max(256, 'Max 256 characters'),
    issuerDescription: Yup.string().nullable().max(2048, 'Max 2048 characters'),
    useOfProceedsDescription: Yup.string().nullable().max(2048, 'Max 2048 characters'),
    dataroomLink: Yup.string().nullable().max(2048, 'Max 2048 characters'),
    figi: Yup.string().nullable().max(64, 'Max 64 characters'),
    bbmgId: Yup.string().nullable().max(64, 'Max 64 characters'),
    loanXId: Yup.string().nullable().max(64, 'Max 64 characters'),
});
